/**
 * ImpersonationBanner — sticky band that surfaces the Connect-As state
 * across every route. Shown only when `useAuth().impersonating` is set;
 * hidden for the admin's own session.
 *
 * Click "Exit impersonation" → POST /api/admin/impersonate/exit → server
 * deletes the impersonation cookie → /me query invalidates → admin's
 * own identity returns → banner unmounts.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { LogOut, UserCircle2 } from 'lucide-react'

import { Button } from '@/components/ui/Button'
import { jsonFetch } from '@/lib/api/jsonFetch'
import { fetchCurrentAuthUser } from '@/shell/services/auth'
import { getHomePath, useAuth } from '@/shell/module-system'

async function exitImpersonation(): Promise<void> {
  await jsonFetch('/api/admin/impersonate/exit', { method: 'POST' })
}

export function ImpersonationBanner() {
  const user = useAuth()
  const queryClient = useQueryClient()

  const exit = useMutation({
    mutationFn: exitImpersonation,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
      // Hard navigate so any module-local state tied to the impersonated
      // identity gets re-fetched cleanly, landing on the admin's own
      // (freshly-restored) home path rather than assuming '/'.
      const adminUser = await fetchCurrentAuthUser()
      window.location.assign(getHomePath(adminUser))
    },
  })

  if (!user.impersonating) return null

  const targetLabel = user.name || user.email || 'unknown user'
  const adminLabel = user.impersonating.adminName || user.impersonating.adminEmail

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center gap-3 border-b border-amber-300 bg-amber-100 px-6 py-2 text-amber-900"
    >
      <UserCircle2 size={18} className="shrink-0" />
      <span className="text-sm">
        <span className="font-semibold">Viewing as {targetLabel}</span>
        <span className="ml-2 text-amber-800">
          (started by {adminLabel} · all actions logged)
        </span>
      </span>
      <div className="ml-auto">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => exit.mutate()}
          disabled={exit.isPending}
        >
          <LogOut size={14} className="mr-1.5" />
          {exit.isPending ? 'Exiting…' : 'Exit impersonation'}
        </Button>
      </div>
    </div>
  )
}
